import type { fabric } from 'fabric';
import type { FabricEditor } from 'modules/fabric-editor';
import type { PlaceholderCornerRadii } from 'modules/manager/domain/placeholder/types';
import { useCallback, useEffect, useMemo, useState } from 'react';

export type Corner = keyof PlaceholderCornerRadii;

export const CORNERS: { key: Corner; label: string }[] = [
  { key: 'tl', label: 'Верх Л' },
  { key: 'tr', label: 'Верх П' },
  { key: 'br', label: 'Низ П' },
  { key: 'bl', label: 'Низ Л' },
];

interface Aggregate {
  value: number | undefined;
  isMixed: boolean;
}

export interface CornerRadiiControl {
  /** Uniform radius across the selection. */
  uniform: Aggregate;
  /** Per-corner radius across the selection (falls back to uniform when unset). */
  corners: Record<Corner, Aggregate>;
  /** True when any selected object carries explicit per-corner radii. */
  hasPerCorner: boolean;
  /** Set the uniform radius and clear any per-corner override (back to uniform). */
  setUniform: (value: number | undefined) => void;
  /** Set one corner, seeding the others from each object's own current radii. */
  setCorner: (corner: Corner, value: number | undefined) => void;
}

/**
 * Per-object corner-radius access strategy. Decouples the shared hook/UI from
 * where each object kind keeps its radii: the image placeholder stores a uniform
 * `data.cornerRadius` + per-corner `data.cornerRadii` and refits after a write; a
 * native rect keeps the uniform value in its `rx`/`ry` props and per-corner in
 * `data.cornerRadii`, applied through `editor.rectCorners`. Both report and seed
 * per-corner radii in the same `PlaceholderCornerRadii` shape, so the aggregation
 * and the `CornerRadiusSection` UI stay kind-agnostic. See
 * `corner-radii-strategies.ts` for the two concrete strategies.
 */
export interface CornerRadiiStrategy {
  /** Uniform radius for one object (what the collapsed field shows/sets). */
  readUniform(obj: fabric.Object): number;
  /** Explicit per-corner radii if the object carries them, else undefined. */
  readPerCorner(obj: fabric.Object): PlaceholderCornerRadii | undefined;
  /** Set the uniform radius and clear any per-corner override (back to uniform). */
  writeUniform(obj: fabric.Object, value: number): void;
  /** Set explicit per-corner radii. */
  writePerCorner(obj: fabric.Object, radii: PlaceholderCornerRadii): void;
}

function aggregate(objects: fabric.Object[], read: (o: fabric.Object) => number): Aggregate {
  if (objects.length === 0) return { value: undefined, isMixed: false };
  const first = read(objects[0]);
  for (let i = 1; i < objects.length; i++) {
    if (read(objects[i]) !== first) return { value: undefined, isMixed: true };
  }
  return { value: first, isMixed: false };
}

/**
 * Reads/writes corner radii for one or more objects of a single kind, driven by
 * a `CornerRadiiStrategy`.
 *
 * Why a bespoke hook instead of `useCommonFabricData<PlaceholderCornerRadii>`:
 * that hook's mixed-check is strict `!==`, so two objects with equal radii but
 * distinct object references read as "mixed". Here each corner is aggregated
 * numerically, and a per-corner write seeds the other three from each object's
 * own current value so a multi-selection with differing radii stays per-object.
 */
export function useCornerRadii(
  objects: fabric.Object[],
  editor: FabricEditor,
  strategy: CornerRadiiStrategy,
): CornerRadiiControl {
  const objectSet = useMemo(() => new Set(objects), [objects]);
  const cornerOf = useCallback(
    (o: fabric.Object, corner: Corner) => strategy.readPerCorner(o)?.[corner] ?? strategy.readUniform(o),
    [strategy],
  );
  const read = useCallback(
    () => ({
      uniform: aggregate(objects, (o) => strategy.readUniform(o)),
      corners: {
        tl: aggregate(objects, (o) => cornerOf(o, 'tl')),
        tr: aggregate(objects, (o) => cornerOf(o, 'tr')),
        br: aggregate(objects, (o) => cornerOf(o, 'br')),
        bl: aggregate(objects, (o) => cornerOf(o, 'bl')),
      },
      hasPerCorner: objects.some((o) => Boolean(strategy.readPerCorner(o))),
    }),
    [objects, strategy, cornerOf],
  );
  const [state, setState] = useState(read);

  useEffect(() => {
    setState(read());
    const off = editor.on('object:modified', (e) => {
      if (objectSet.has(e.target as fabric.Object)) setState(read());
    });
    return () => off();
  }, [editor, objectSet, read]);

  const setUniform = useCallback(
    (value: number | undefined) => {
      for (const o of objects) strategy.writeUniform(o, value ?? 0);
      setState(read());
    },
    [objects, strategy, read],
  );

  const setCorner = useCallback(
    (corner: Corner, value: number | undefined) => {
      const next = value ?? 0;
      for (const o of objects) {
        const u = strategy.readUniform(o);
        const base = strategy.readPerCorner(o) ?? { tl: u, tr: u, br: u, bl: u };
        strategy.writePerCorner(o, { ...base, [corner]: next });
      }
      setState(read());
    },
    [objects, strategy, read],
  );

  return { ...state, setUniform, setCorner };
}
